import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Clock, Lock, Unlock, TrendingUp, ChevronDown, ChevronUp,
  User, Calendar, DollarSign, CheckCircle2, AlertCircle,
  Zap, Gift, ArrowRight
} from 'lucide-react';

// Payout status constants matching backend
const PayoutStatus = {
  TRIAL: 'trial',
  PENDING: 'pending',
  RELEASED: 'released',
  RESIDUAL: 'residual',
  CANCELLED: 'cancelled'
};

// Stage configuration
const STAGES = [
  { 
    key: PayoutStatus.TRIAL, 
    label: 'Trial', 
    shortLabel: '30 Days Free',
    icon: Gift, 
    color: 'blue',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500',
    description: 'Free trial period - no payout yet'
  },
  { 
    key: PayoutStatus.PENDING, 
    label: 'Pending', 
    shortLabel: 'Month 1 Held',
    icon: Lock, 
    color: 'yellow',
    bgColor: 'bg-yellow-500',
    textColor: 'text-yellow-400',
    borderColor: 'border-yellow-500',
    description: 'First payment received, bonus held'
  },
  { 
    key: PayoutStatus.RELEASED, 
    label: 'Released', 
    shortLabel: 'Month 2 Paid',
    icon: Unlock, 
    color: 'green',
    bgColor: 'bg-green-500',
    textColor: 'text-green-400',
    borderColor: 'border-green-500',
    description: 'Month 1 + 2 bonus released'
  },
  { 
    key: PayoutStatus.RESIDUAL, 
    label: 'Residual', 
    shortLabel: '8% Ongoing',
    icon: TrendingUp, 
    color: 'purple',
    bgColor: 'bg-purple-500',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500',
    description: '8% commission on each payment'
  }
];

function getStageIndex(status) {
  const index = STAGES.findIndex(s => s.key === status);
  return index >= 0 ? index : 0;
}

function getStageConfig(status) {
  return STAGES.find(s => s.key === status) || STAGES[0];
}

function calculateDaysInStage(signupDate, currentMonth) {
  if (!signupDate) return 0;
  
  try {
    const signup = new Date(signupDate);
    const now = new Date();
    const daysSinceSignup = Math.floor((now - signup) / (1000 * 60 * 60 * 24));
    
    if (currentMonth === 0) {
      // Trial: days remaining out of 30
      return Math.max(0, 30 - daysSinceSignup);
    } else {
      // After trial: days into current month
      const daysAfterTrial = daysSinceSignup - 30;
      return daysAfterTrial % 30;
    }
  } catch {
    return 0;
  }
}

// Individual referral tracker card
function ReferralPayoutCard({ referral, expanded, onToggle }) {
  const stageIndex = getStageIndex(referral.payout_status);
  const stageConfig = getStageConfig(referral.payout_status);
  const daysRemaining = calculateDaysInStage(referral.signup_date, referral.current_month);
  
  const tier = referral.tier || 'pro';
  const tierLabel = tier === 'elite' ? 'Elite' : 'Pro';
  const tierColor = tier === 'elite' ? 'text-purple-400' : 'text-amber-400';
  
  return (
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
      {/* Header - Always visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${stageConfig.bgColor}/20 flex items-center justify-center`}>
              <stageConfig.icon className={`w-5 h-5 ${stageConfig.textColor}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-white">{referral.stylist_name}</p>
                <Badge className={`${tier === 'elite' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'} text-xs`}>
                  {tierLabel}
                </Badge>
              </div>
              <p className="text-xs text-zinc-500">{referral.stylist_email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className={`font-bold ${stageConfig.textColor}`}>
                ${referral.total_earned?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-zinc-500">earned</p>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-zinc-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-500" />
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center gap-1">
            {STAGES.map((stage, idx) => {
              const isCompleted = idx < stageIndex;
              const isCurrent = idx === stageIndex;
              const isPending = idx > stageIndex;
              
              return (
                <div key={stage.key} className="flex-1 flex items-center">
                  {/* Stage dot */}
                  <div className={`
                    w-3 h-3 rounded-full flex-shrink-0 transition-all
                    ${isCompleted ? stage.bgColor : ''}
                    ${isCurrent ? `${stage.bgColor} ring-2 ring-offset-2 ring-offset-zinc-900 ring-${stage.color}-400` : ''}
                    ${isPending ? 'bg-zinc-700' : ''}
                  `} />
                  
                  {/* Connector line */}
                  {idx < STAGES.length - 1 && (
                    <div className={`
                      flex-1 h-1 mx-1 rounded
                      ${isCompleted ? stage.bgColor : 'bg-zinc-700'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Stage labels */}
          <div className="flex justify-between mt-2">
            {STAGES.map((stage, idx) => {
              const isCurrent = idx === stageIndex;
              return (
                <p 
                  key={stage.key} 
                  className={`text-[10px] ${isCurrent ? stage.textColor + ' font-semibold' : 'text-zinc-600'}`}
                >
                  {stage.shortLabel}
                </p>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-zinc-800 pt-4 space-y-4">
          {/* Current Status */}
          <div className={`p-3 rounded-lg ${stageConfig.bgColor}/10 border ${stageConfig.borderColor}/30`}>
            <div className="flex items-center gap-2 mb-2">
              <stageConfig.icon className={`w-4 h-4 ${stageConfig.textColor}`} />
              <span className={`font-semibold ${stageConfig.textColor}`}>
                Current Stage: {stageConfig.label}
              </span>
            </div>
            <p className="text-sm text-zinc-400">{stageConfig.description}</p>
            
            {referral.payout_status === PayoutStatus.TRIAL && (
              <p className="text-xs text-blue-300 mt-2">
                <Clock className="w-3 h-3 inline mr-1" />
                {daysRemaining} days remaining in trial
              </p>
            )}
          </div>
          
          {/* Payout Breakdown */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500 mb-1">Month 1 Bonus</p>
              <p className="font-bold text-white">
                ${referral.month1_amount?.toFixed(2) || (tier === 'elite' ? '15.00' : '10.00')}
              </p>
              {referral.payout_status === PayoutStatus.PENDING && (
                <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px] mt-1">
                  <Lock className="w-2 h-2 mr-1" /> Held
                </Badge>
              )}
              {(referral.payout_status === PayoutStatus.RELEASED || referral.payout_status === PayoutStatus.RESIDUAL) && (
                <Badge className="bg-green-500/20 text-green-400 text-[10px] mt-1">
                  <CheckCircle2 className="w-2 h-2 mr-1" /> Paid
                </Badge>
              )}
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500 mb-1">Month 2 Bonus</p>
              <p className="font-bold text-white">
                ${referral.month2_amount?.toFixed(2) || (tier === 'elite' ? '15.00' : '10.00')}
              </p>
              {(referral.payout_status === PayoutStatus.RELEASED || referral.payout_status === PayoutStatus.RESIDUAL) && (
                <Badge className="bg-green-500/20 text-green-400 text-[10px] mt-1">
                  <CheckCircle2 className="w-2 h-2 mr-1" /> Paid
                </Badge>
              )}
              {(referral.payout_status === PayoutStatus.TRIAL || referral.payout_status === PayoutStatus.PENDING) && (
                <Badge className="bg-zinc-600/20 text-zinc-500 text-[10px] mt-1">
                  Upcoming
                </Badge>
              )}
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500 mb-1">Held Amount</p>
              <p className={`font-bold ${referral.held_amount > 0 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                ${referral.held_amount?.toFixed(2) || '0.00'}
              </p>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500 mb-1">Released</p>
              <p className={`font-bold ${referral.released_amount > 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                ${referral.released_amount?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
          
          {/* Residual Info (if applicable) */}
          {referral.payout_status === PayoutStatus.RESIDUAL && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                <span className="font-semibold text-purple-400">Residual Active</span>
              </div>
              <p className="text-sm text-zinc-400">
                Earning {referral.residual_rate || 8}% on each monthly payment
              </p>
              <p className="text-xs text-purple-300 mt-1">
                Last residual: ${referral.last_residual_amount?.toFixed(2) || '0.00'}
              </p>
            </div>
          )}
          
          {/* Timeline */}
          <div className="space-y-2">
            <p className="text-xs text-zinc-500 font-semibold">Timeline</p>
            <div className="flex items-center gap-2 text-xs">
              <Calendar className="w-3 h-3 text-zinc-500" />
              <span className="text-zinc-400">Signed up:</span>
              <span className="text-white">
                {referral.signup_date ? new Date(referral.signup_date).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            {referral.trial_end_date && (
              <div className="flex items-center gap-2 text-xs">
                <Clock className="w-3 h-3 text-zinc-500" />
                <span className="text-zinc-400">Trial ends:</span>
                <span className="text-white">
                  {new Date(referral.trial_end_date).toLocaleDateString()}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs">
              <DollarSign className="w-3 h-3 text-zinc-500" />
              <span className="text-zinc-400">Payments received:</span>
              <span className="text-white">{referral.payments_received || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Payout Tracker Component
export default function SoldierPayoutTracker({ payoutSummary, loading }) {
  const [expandedId, setExpandedId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  
  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-zinc-800 rounded w-1/3"></div>
            <div className="h-20 bg-zinc-800 rounded"></div>
            <div className="h-20 bg-zinc-800 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!payoutSummary || !payoutSummary.referrals_detail || payoutSummary.referrals_detail.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            Payout Tracker
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Gift className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No referrals yet</p>
            <p className="text-zinc-600 text-sm mt-1">Share your referral code to start earning!</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const referrals = payoutSummary.referrals_detail;
  const displayedReferrals = showAll ? referrals : referrals.slice(0, 5);
  
  // Count by status
  const statusCounts = {
    trial: referrals.filter(r => r.payout_status === PayoutStatus.TRIAL).length,
    pending: referrals.filter(r => r.payout_status === PayoutStatus.PENDING).length,
    released: referrals.filter(r => r.payout_status === PayoutStatus.RELEASED).length,
    residual: referrals.filter(r => r.payout_status === PayoutStatus.RESIDUAL).length,
  };
  
  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            Payout Tracker
          </CardTitle>
          <div className="flex items-center gap-2">
            {statusCounts.trial > 0 && (
              <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                {statusCounts.trial} in trial
              </Badge>
            )}
            {statusCounts.pending > 0 && (
              <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                {statusCounts.pending} pending
              </Badge>
            )}
            {statusCounts.residual > 0 && (
              <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                {statusCounts.residual} residual
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {STAGES.map(stage => (
            <div 
              key={stage.key}
              className={`p-2 rounded-lg ${stage.bgColor}/10 border ${stage.borderColor}/20 text-center`}
            >
              <stage.icon className={`w-4 h-4 ${stage.textColor} mx-auto mb-1`} />
              <p className={`text-lg font-bold ${stage.textColor}`}>
                {statusCounts[stage.key] || 0}
              </p>
              <p className="text-[10px] text-zinc-500">{stage.label}</p>
            </div>
          ))}
        </div>
        
        {/* Referral Cards */}
        <div className="space-y-3">
          {displayedReferrals.map((referral) => (
            <ReferralPayoutCard
              key={referral.referral_id}
              referral={referral}
              expanded={expandedId === referral.referral_id}
              onToggle={() => setExpandedId(
                expandedId === referral.referral_id ? null : referral.referral_id
              )}
            />
          ))}
        </div>
        
        {/* Show More Button */}
        {referrals.length > 5 && (
          <div className="text-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="border-zinc-700 text-zinc-400 hover:text-white"
            >
              {showAll ? (
                <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
              ) : (
                <>View All {referrals.length} Referrals <ChevronDown className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
